import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { BookOpen, ArrowLeft, Layers } from 'lucide-react';

const typeLabel = (type) => {
  if (type === 'practical') return 'Practical';
  if (type === 'both') return 'Both';
  return 'Theory';
};

const SubjectDetail = () => {
  const { id } = useParams();
  const [subject, setSubject] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSubject = async () => {
    if (!id) return;
    try {
      const res = await api.get(`/subjects/${id}`);
      setSubject(res.data?.data ?? null);
    } catch (e) {
      console.error(e);
      setSubject(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubject();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!subject) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" asChild className="text-muted-foreground">
          <Link to="/subjects"><ArrowLeft className="w-4 h-4 mr-2" /> Back to Subjects</Link>
        </Button>
        <Card className="border-border">
          <CardContent className="py-12 text-center text-muted-foreground">Subject not found</CardContent>
        </Card>
      </div>
    );
  }

  const classes = subject.classes || [];
  const assignedCount = subject.assigned_classes_count ?? classes.length;

  return (
    <div className="space-y-6" data-testid="subject-detail-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <Button variant="ghost" asChild className="text-muted-foreground w-fit">
          <Link to="/subjects"><ArrowLeft className="w-4 h-4 mr-2" /> Back to Subjects</Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-blue-400" />
            {subject.name}
          </h1>
          {subject.code && (
            <p className="text-muted-foreground text-sm mt-1">Code: {subject.code}</p>
          )}
        </div>
      </div>

      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-foreground text-lg">Subject Information</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">Subject Name</p>
            <p className="text-foreground font-medium">{subject.name}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Subject Code</p>
            <p className="text-foreground font-medium">{subject.code || '—'}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Type</p>
            <p className="text-foreground font-medium">{typeLabel(subject.type)}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Status</p>
            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium capitalize ${
              subject.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-muted text-muted-foreground'
            }`}>
              {subject.status || 'active'}
            </span>
          </div>
          <div className="sm:col-span-2">
            <p className="text-muted-foreground">Description</p>
            <p className="text-foreground">{subject.description || '—'}</p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-foreground text-lg flex items-center gap-2">
            <Layers className="w-5 h-5 text-blue-400" />
            Mapping Summary
          </CardTitle>
          <p className="text-muted-foreground text-sm font-normal">
            This subject is assigned to {assignedCount} class{assignedCount === 1 ? '' : 'es'}.
          </p>
        </CardHeader>
        <CardContent>
          {classes.length === 0 ? (
            <p className="text-muted-foreground py-2">No classes assigned to this subject yet.</p>
          ) : (
            <ul className="space-y-2">
              {classes.map((cls) => (
                <li key={cls.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <Link to={`/classes/${cls.id}`} className="text-blue-400 hover:text-blue-300 font-medium">
                    {cls.name}
                  </Link>
                  <span className={`text-xs px-2 py-0.5 rounded capitalize ${
                    cls.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-muted text-muted-foreground'
                  }`}>
                    {cls.status || 'active'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubjectDetail;
